/** @module Watches the guide frame and shoots when a card inside it holds still. */
import { type RefObject, useEffect, useRef, useState } from "react";

import {
  type Box,
  contrastAround,
  crop,
  detailOf,
  fingerprintDistance,
  fingerprintOf,
  MIN_NOVELTY,
  motionBetween,
  outside,
  type Readiness,
  STEADY_FRAMES,
  SteadyFrame,
  toGray,
} from "@/features/scan/auto-capture";
import { guideRect, type Rect } from "@/features/scan/guide-frame";
import { log } from "@/lib/client-log";

/** Frames per second; more only heats the phone without helping. */
export const FPS = 8;
/**
 * The card is judged at roughly this width, whatever the camera delivers.
 *
 * Coarser is better: at 120 pixels a trembling hand moves the card by several pixels
 * between frames and every one of them counts as motion. Deriving the thumbnail size
 * from the card instead of from the picture keeps the numbers comparable between a
 * phone holding portrait frames and a camera delivering landscape ones.
 */
const CARD_WIDTH = 56;
/** Sampling the whole picture is what makes the frame comparable to its surroundings. */
const MAX_SAMPLE_WIDTH = 220;
/** How often the raw measurements go into the log; every frame would drown it. */
const LOG_EVERY = 40;
/** Frames the whole picture may differ before the remembered surface is thrown away. */
const CAMERA_MOVED_FRAMES = 8;

/** What the detector measured when it fired; kept with the photo to tune it later. */
export interface ShotReading {
  motion: number;
  detail: number;
  contrast: number;
  change: number | null;
  baseline: number;
  /** Rate the loop actually ran at; a busy phone runs it slower than asked. */
  fps: number | null;
  /** Time from something appearing in the frame to the shot. */
  settle_ms: number;
  /** Difference from the remembered surface, or null when none was taken. */
  novelty: number | null;
  /** How much the surroundings moved since the last photo; small means a fixed camera. */
  surround: number | null;
  /** What armed the shutter: the start, an empty frame, a new card, or a new card on a fixed camera. */
  rearm: "start" | "empty" | "change" | "fixed";
  /** Empty frames in a row before this card appeared. */
  empty_frames: number;
}

export interface AutoCaptureState {
  readiness: Readiness;
  progress: number;
  /** Whether a surface is remembered right now; the screen says so. */
  surface: boolean;
  /** Difference from that surface, 0 when none is remembered. */
  novelty: number;
  /** Live measurements, shown in the diagnostics line and sent to the server log. */
  motion: number;
  detail: number;
  contrast: number;
  /** Difference to the last photo; see REARM_CHANGE. */
  change: number;
  baseline: number;
  /** Rate the loop actually runs at; null until it has been measured once. */
  fps: number | null;
  /** Size of the camera picture, e.g. "2160x3840"; tells whether the phone gave its best. */
  video: string;
}

const IDLE: AutoCaptureState = {
  readiness: "empty",
  progress: 0,
  surface: false,
  novelty: 0,
  motion: 0,
  detail: 0,
  contrast: 0,
  change: 0,
  baseline: 0,
  fps: null,
  video: "",
};

export function useAutoCapture(
  video: RefObject<HTMLVideoElement | null>,
  enabled: boolean,
  onShoot: (reading: ShotReading) => void,
  /** Changing this arms the detector again, e.g. after a capture failed. */
  armAgain = 0,
  /**
   * Where the frame is, in fractions of the video, when the screen decides it.
   *
   * A full-screen camera crops the video, so a frame derived from the video alone
   * would not be where the person sees it.
   */
  guide?: Rect,
  /**
   * Changing this remembers the empty surface; 0 means none is remembered.
   *
   * Only useful when the camera does not move: in a holder or on a table the background
   * is constant, and then "something lies there" needs no guessing at all.
   */
  rememberAt = 0,
  /** How long the card has to lie still before the shot; see SteadyFrame. */
  steadyFrames = STEADY_FRAMES,
  /** Counts photos taken by hand; each one marks the card in the frame as done. */
  manualShots = 0,
): AutoCaptureState {
  const [state, setState] = useState<AutoCaptureState>(IDLE);
  const shoot = useRef(onShoot);
  // Read inside the loop: a new rectangle must not restart the detector and lose its count.
  const frame = useRef(guide);
  const manual = useRef(manualShots);

  useEffect(() => {
    shoot.current = onShoot;
  }, [onShoot]);

  useEffect(() => {
    frame.current = guide;
  }, [guide]);

  useEffect(() => {
    manual.current = manualShots;
  }, [manualShots]);

  useEffect(() => {
    if (!enabled) return;

    const steady = new SteadyFrame(steadyFrames);
    const canvas = document.createElement("canvas");
    let previous: Uint8Array | null = null;
    // What the frame looked like when the last photo was taken.
    let lastShot: Float32Array | null = null;
    // Everything around the guide frame at that moment.
    let lastAround: Uint8Array | null = null;
    let stopped = false;
    let frames = 0;
    let sampledAt = performance.now();
    let fps: number | null = null;
    // When the frame last went from empty to holding something; the settle time counts from here.
    let appearedAt: number | null = null;
    // The remembered empty surface, inside the guide frame and around it.
    let surface: { inside: Uint8Array; around: Uint8Array } | null = null;
    let remembered = 0;
    let movedFrames = 0;
    let manualSeen = manual.current;

    const timer = setInterval(() => {
      const element = video.current;
      // readyState below HAVE_CURRENT_DATA means there is no picture to read yet,
      // even though the element already reports a size.
      if (stopped || !element || element.videoWidth === 0 || element.readyState < 2) return;

      const rect = frame.current ?? guideRect(element.videoWidth, element.videoHeight);
      const width = Math.min(MAX_SAMPLE_WIDTH, Math.round(CARD_WIDTH / rect.width));
      const height = Math.max(2, Math.round((width * element.videoHeight) / element.videoWidth));
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;
      context.drawImage(element, 0, 0, width, height);

      const pixels = context.getImageData(0, 0, width, height).data;
      const gray = toGray(pixels);
      const guide: Box = {
        x: Math.round(rect.x * width),
        y: Math.round(rect.y * height),
        width: Math.max(2, Math.round(rect.width * width)),
        height: Math.max(2, Math.round(rect.height * height)),
      };
      const inside = crop(gray, width, guide);
      const print = fingerprintOf(pixels, width, guide);
      const around = outside(gray, width, guide);

      if (rememberAt !== remembered) {
        remembered = rememberAt;
        surface = rememberAt === 0 ? null : { inside, around: around.slice() };
        movedFrames = 0;
        steady.reset();
        log("scan.surface", { remembered: surface !== null });
      }

      // The surface only holds while the camera holds: if everything outside the frame
      // changes too, the phone was picked up and the memory is worse than none. Only the
      // outside counts: a card in the frame changes the whole picture, and that is the
      // very thing the memory is for (it used to drop it and remember the card instead).
      if (
        surface?.around.length === around.length &&
        motionBetween(surface.around, around) >= MIN_NOVELTY
      ) {
        movedFrames += 1;
        if (movedFrames >= CAMERA_MOVED_FRAMES) {
          // Gone for good until the person remembers it again; taking the current
          // picture instead would make whatever lies in the frame the new "empty".
          surface = null;
          log("scan.surface_dropped", {});
        }
      } else {
        movedFrames = 0;
      }

      const stats = {
        motion: previous ? motionBetween(previous, inside) : 255,
        detail: detailOf(inside, guide.width, guide.height),
        contrast: contrastAround(gray, width, height, guide),
        change: lastShot ? fingerprintDistance(lastShot, print) : undefined,
        novelty: surface ? motionBetween(surface.inside, inside) : undefined,
        surround: lastAround ? motionBetween(lastAround, around) : undefined,
      };
      previous = inside;

      if (manual.current !== manualSeen) {
        manualSeen = manual.current;
        lastShot = print;
        lastAround = around;
        steady.markTaken();
      }

      if (frames % LOG_EVERY === 0) {
        let darkest = 255;
        let brightest = 0;
        for (const value of gray) {
          darkest = Math.min(darkest, value);
          brightest = Math.max(brightest, value);
        }
        // A busy phone runs the loop slower than asked; the shutter then takes longer too.
        const now = performance.now();
        fps = frames === 0 ? null : Math.round((LOG_EVERY * 10000) / (now - sampledAt)) / 10;
        sampledAt = now;
        log("scan.sample", {
          fps,
          video: `${element.videoWidth}x${element.videoHeight}`,
          sample: `${width}x${height}`,
          guide: `${guide.width}x${guide.height}`,
          darkest,
          brightest,
          paused: element.paused,
        });
      }
      frames += 1;

      const fire = steady.accept(stats);
      if (steady.readiness === "empty") appearedAt = null;
      else appearedAt ??= performance.now();
      setState({
        readiness: steady.readiness,
        progress: steady.progress,
        surface: surface !== null,
        novelty: stats.novelty ?? 0,
        motion: stats.motion,
        detail: stats.detail,
        contrast: stats.contrast,
        change: stats.change ?? 0,
        baseline: steady.baseline,
        fps,
        video: `${element.videoWidth}x${element.videoHeight}`,
      });
      log(
        "scan.frame",
        {
          motion: Math.round(stats.motion * 10) / 10,
          detail: Math.round(stats.detail * 10) / 10,
          contrast: Math.round(stats.contrast * 10) / 10,
          change: stats.change === undefined ? null : Math.round(stats.change),
          novelty: stats.novelty === undefined ? null : Math.round(stats.novelty * 10) / 10,
          baseline: Math.round(steady.baseline * 10) / 10,
          readiness: steady.readiness,
          fire,
        },
        // Every frame is only for a deliberate debugging session.
        "debug",
      );
      if (fire) {
        lastShot = print;
        lastAround = around;
        const round = (value: number) => Math.round(value * 10) / 10;
        shoot.current({
          motion: round(stats.motion),
          detail: round(stats.detail),
          contrast: round(stats.contrast),
          change: stats.change === undefined ? null : round(stats.change),
          baseline: round(steady.baseline),
          novelty: stats.novelty === undefined ? null : round(stats.novelty),
          surround: stats.surround === undefined ? null : round(stats.surround),
          fps,
          settle_ms: appearedAt === null ? 0 : Math.round(performance.now() - appearedAt),
          rearm: steady.rearm.by,
          empty_frames: steady.rearm.emptyFrames,
        });
        appearedAt = null;
      }
    }, 1000 / FPS);

    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [enabled, video, armAgain, rememberAt, steadyFrames]);

  // Deriving the idle case avoids a state update just to clear the overlay.
  return enabled ? state : IDLE;
}
